/* eslint-disable @typescript-eslint/no-explicit-any */
import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  PaymentControllerService,
  PersonDetail,
  Reward,
  RewardControllerService,
  ShippingOrderControllerService,
  ShippingOrderItemDetail,
  SubscriberCancellationReason,
  SubscriberCancellationRequest,
  SubscriberControllerService,
  SubscriberCoupon,
  SubscriberEditionCompositionSummary,
  SubscriberInfo,
  SubscriberPaymentDetail,
  SubscriptionControllerService,
  SubscriptionType
} from 'src/app/admin-api';
import {
  EditionId,
  getAllSubscriptions,
  getSubscriptionName,
  PaymentHistory
} from 'src/app/models';
import { AvailableBilling } from 'src/app/models/availableBilling';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { GatewayUtil } from 'src/app/utils/gateway.util';
import { environment } from 'src/environments/environment';
import { DropdownFilter, TableColumn } from '../table';

@Component({
  selector: 'app-subscriber-cancellation-modal',
  templateUrl: './subscriber-cancellation-modal.component.html',
  styleUrls: ['./subscriber-cancellation-modal.component.scss'],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None
})
export class SubscriberCancellationModalComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  public ref = inject(DynamicDialogRef);
  private subscriberService = inject(SubscriberControllerService);
  private subscriptionService = inject(SubscriptionControllerService);
  private datePipe = inject(DatePipe);
  private confirmationService = inject(ConfirmationService);
  private paymentService = inject(PaymentControllerService);
  private clipboard = inject(Clipboard);
  private messageService = inject(MessageService);
  private shippingOrderService = inject(ShippingOrderControllerService);
  private rewardService = inject(RewardControllerService);

  refundStatusList = ['charged_back', 'refunded', 'chargedback'];

  subscriber: SubscriberInfo;
  person: PersonDetail;
  cancellationReasons: SubscriberCancellationReason[];
  formGroup = new FormGroup({
    subscriberId: new FormControl(
      this.config.data.subscriber?.subscriberId,
      Validators.required
    ),
    cancellationReasonId: new FormControl(undefined, Validators.required),
    cancellationReason: new FormControl(undefined, [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(4000)
    ]),
    automaticSelection: new FormControl(true),
    refundGateway: new FormControl(false)
  });
  billings: AvailableBilling[];
  availableEditions: AvailableBilling[];
  selectedEditions: AvailableBilling[] = [];
  paymentHistory: PaymentHistory[] | undefined;
  payment: any;
  compositions: SubscriberEditionCompositionSummary[] | undefined;
  subscriptionTypes: SubscriptionType[];
  shippingOrderItems: ShippingOrderItemDetail[];
  shippingOrderItemsAvailable: ShippingOrderItemDetail[];
  ordersColumns = [
    new TableColumn.Builder()
      .setHeader('Referencia')
      .setField('productVariantName')
      .setRouterLink('/products/catalog/product-variant/')
      .setRouterLinkFieldName('productVariantId')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo')
      .setField('shippingOrderTypeName')
      .build(),
    new TableColumn.Builder()
      .setHeader('Edição')
      .setField('subscriberEditionId')
      .setDisplayFunction((item: ShippingOrderItemDetail) =>
        this.edition(item.subscriberEditionId)
      )
      .setFilter(true)
      .build(),
    new TableColumn.Builder()
      .setHeader('Glampoints')
      .setField('salePoint')
      .setCondition('gte')
      .setDisplayFunction((item: ShippingOrderItemDetail) =>
        item.shippingOrderTypeId === 3
          ? -item.salePoint
          : item.shippingOrderTypeId === undefined
            ? item.salePoint
            : null
      )
      .build()
  ];
  showOrders = false;
  dropdownFilters: {
    [field: string]: Array<DropdownFilter>;
  } = {};
  coupons: SubscriberCoupon[];
  rewards: Reward[];
  badgeReward: Reward;
  /** 409/417 já gravados: operador precisa confirmar; bloqueia dismiss e re-POST. */
  requireOutcomeAck = false;

  async ngOnInit() {
    this.subscriber = this.config.data.subscriber;
    this.cancellationReasons = this.config.data.cancellationReasons;
    this.billings = this.config.data.billings || [];
    this.person = this.config.data.person;
    if (!this.subscriber)
      throw 'SubscriberCancellationModalComponent: Please provide subscriber';
    if (!this.cancellationReasons || !this.cancellationReasons.length)
      throw 'SubscriberCancellationModalComponent: Please provide cancellationReasons';
    this.paymentHistory = this.config.data.paymentHistory;
    this.compositions = this.config.data.compositions;
    this.billings = [
      ...this.billings.filter((b) =>
        this.lastPayment?.billings?.some(
          (pb) => pb.subscriberBillingId === b.subscriberBillingId
        )
      )
    ];
    this.billings?.sort(
      (b1, b2) => (b1.editionId as number) - (b2.editionId as number)
    );
    await this.findSubscriptionTypes();
    this.autoSelectEditions();
    await this.findShippingOrders();
    await Promise.all([
      this.findCoupons(),
      this.findPaymentReward(),
      this.findBadgeRewards()
    ]);
    this.setItemsToRefund();
    if (this.lastPayment?.transactionId) this.findTID();
  }

  async findSubscriptionTypes() {
    try {
      this.subscriptionTypes = (
        await Promise.all(
          getAllSubscriptions().map((s) =>
            lastValueFrom(
              this.subscriptionService
                .findSubscriptionTypeList(s.value)
                .pipe(map((data) => data.result))
            )
          )
        )
      ).reduce((list, sTypes) => list.concat(sTypes), []);
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    }
  }

  async findTID() {
    if (this.lastPayment?.transactionId)
      try {
        delete this.payment;
        this.payment = await lastValueFrom(
          this.paymentService
            .getTransactionByCreditCardPaymentId(this.lastPayment.paymentId)
            .pipe(map((data) => data.result))
        );
      } catch (error) {
        this.payment = null;
        console.error(error);
      }
  }

  async submit(noRefund = false, ignoreGatewayStatus = false): Promise<void> {
    if (this.requireOutcomeAck) {
      return;
    }
    // Se não selecionou billing para estornar
    if (!this.selectedEditions?.length && this.billings?.length && !noRefund) {
      this.confirmationService.confirm({
        acceptLabel: 'Sim, somente cancelar',
        rejectLabel: 'Voltar',
        acceptButtonStyleClass: 'p-button-info',
        message:
          'Deseja realizar o cancelamento sem solicitar o estorno das edições?',
        header: 'Cancelamento sem estorno',
        accept: () => this.submit(true)
      });
      return;
    }
    if (
      !this.formGroup.value.refundGateway &&
      this.selectedEditions?.length &&
      !this.refundedTransaction &&
      !this.pendingAnnualMonthly &&
      this.lastPayment?.amount &&
      !this.pix &&
      !ignoreGatewayStatus
    ) {
      this.confirmationService.confirm({
        acceptLabel: 'Sim, farei o estorno depois',
        rejectLabel: 'Voltar',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-info',
        header: 'Avançar sem estorno',
        message: 'Deseja remover as edições sem realizar o estorno no gateway?',
        accept: () => this.submit(false, true)
      });
      return;
    }
    if (this.formGroup.valid) {
      LoaderService.showLoader();
      try {
        const subscriber = await lastValueFrom(
          this.subscriberService
            .cancelSubscriber({
              ...this.formGroup.value,
              fee: this.fee || 0,
              refundAmount:
                !this.hasFee ||
                !this.pendingAnnualMonthly ||
                this.refundedTransaction
                  ? this.refundAmount || 0
                  : 0,
              chargeAmount:
                this.hasFee || this.hasRenewalFee ? this.amountToCharge : 0,
              subscriberBillingIds:
                this.selectedEditions?.map((s) => s.subscriberBillingId) || []
            } as SubscriberCancellationRequest)
            .pipe(map((data) => data.result))
        );
        this.ref.close(subscriber);
      } catch (error) {
        LoaderService.showLoader(false);
        // MaIA / admin-api: 409 PENDING_VERIFICATION e 417 GATEWAY_PARTIAL já
        // gravaram o cancelamento — NÃO oferecer "cancelar sem estorno" (repete
        // o POST e pode confundir). Só o 417 sem outcome (estorno recusado
        // antes de gravar) mantém o fluxo antigo de confirmar sem gateway.
        if (this.handleRecordedCancellationOutcome(error)) {
          return;
        }
        if (error.status === 417) {
          this.confirmationService.confirm({
            acceptLabel: 'Sim, confirmar cancelamento',
            rejectLabel: 'Não, voltar',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-info',
            message:
              'Não foi possível realizar o estorno automático. Deseja seguir com o cancelamento da assinatura?<br>' +
              (this.lastPayment?.creditCardId
                ? `Lembre-se de realizar o estorno manualmente no ${this.lastPayment?.gatewayName || 'gateway'} ou solicitar débito em conta ao financeiro.`
                : 'Lembre-se de solicitar o débito em conta do valor do estorno ao financeiro'),
            header: 'Falha no estorno',
            accept: async () => {
              this.formGroup.controls.refundGateway.setValue(false);
              await this.submit(noRefund, true);
            }
          });
          return;
        }
        AppDialogService.showErrorDialog(
          error,
          false,
          'Não foi possível cancelar a assinatura.'
        );
      }
    }
  }

  /**
   * 409 PENDING_VERIFICATION / 417 GATEWAY_PARTIAL: cancelamento já gravado
   * com corpo {@code cancellationOutcome}. Fecha o modal com o assinante.
   * @returns true se tratou o erro (caller não deve seguir).
   */
  private handleRecordedCancellationOutcome(error: any): boolean {
    const result = error?.error?.result;
    const outcome = result?.cancellationOutcome;
    const refundStatus = outcome?.refundStatus as string | undefined;
    const recorded =
      outcome?.cancellationRecorded === true ||
      refundStatus === 'PENDING_VERIFICATION' ||
      refundStatus === 'GATEWAY_PARTIAL';
    if (!recorded) {
      return false;
    }
    if (error.status !== 409 && error.status !== 417) {
      return false;
    }
    const pending =
      refundStatus === 'PENDING_VERIFICATION' ||
      (error.status === 409 && refundStatus !== 'GATEWAY_PARTIAL');
    const message =
      outcome?.message ||
      error?.error?.message ||
      (pending
        ? 'Cancelamento gravado, mas o estorno ficou sem confirmação no gateway (pode ter saído). Conferir no gateway — não repetir o estorno.'
        : 'Cancelamento gravado com estorno parcial. O restante ficou pendente para o financeiro — não estornar de novo o que já foi confirmado.');
    this.requireOutcomeAck = true;
    this.config.closable = false;
    this.config.closeOnEscape = false;
    this.config.dismissableMask = false;
    const closeRecorded = () => this.ref.close(result);
    this.confirmationService.confirm({
      acceptLabel: 'Entendi',
      rejectVisible: false,
      closeOnEscape: false,
      dismissableMask: false,
      acceptButtonStyleClass: 'p-button-info',
      header: pending
        ? 'Cancelamento gravado — conferir estorno'
        : 'Cancelamento gravado — estorno parcial',
      message,
      accept: closeRecorded,
      reject: closeRecorded
    });
    return true;
  }

  billingLabel(billing: AvailableBilling): string {
    return (
      (getSubscriptionName(
        EditionId.subscriptionId(billing.editionId)
      ) as string) +
      ' ' +
      this.datePipe.transform(
        new Date(
          `${EditionId.year(billing.editionId)}-${EditionId.month(billing.editionId).toString().padStart(2, '0')}-01 12:00:00`
        ),
        'MMMM/yyyy',
        undefined,
        'pt-BR'
      ) +
      (billing.delivered ? ' (Entregue)' : '')
    );
  }

  autoSelectEditions() {
    if (this.lastPayment?.amount && this.formGroup.value.automaticSelection) {
      this.selectedEditions = this.billings.filter(
        (b) =>
          (!this.hasFee && !this.hasRenewalFee) ||
          (this.restored &&
            !this.restoredNoFee &&
            (!this.editionFromBilling(b.editionId) ||
              this.editionFromBilling(b.editionId).subscriberEditionStatusId <=
                0 ||
              this.editionFromBilling(b.editionId).subscriberEditionStatusId >
                7)) ||
          EditionId.isAfter(
            b.editionId,
            EditionId.currentEdition(EditionId.subscriptionId(b.editionId))
          ) ||
          (this.billings.length === 1 &&
            EditionId.isAfter(
              b.editionId,
              EditionId.currentEdition(EditionId.subscriptionId(b.editionId))
            ))
      );
      this.availableEditions = [
        ...this.billings.filter((b) =>
          this.selectedEditions.every(
            (e) => e.subscriberBillingId !== b.subscriberBillingId
          )
        )
      ];
    } else if (!this.availableEditions) {
      this.availableEditions = [...(this.billings || [])];
    }
    if (this.shippingOrderItemsAvailable) this.setItemsToRefund();
  }

  diffDays(time: number) {
    return time / (1000 * 60 * 60 * 24);
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Copiado para área de trasnferência'
    });
  }

  editionFromBilling(editionId: number) {
    return this.compositions.find((c) => c.editionId === editionId);
  }

  async findShippingOrders() {
    try {
      this.shippingOrderItemsAvailable = await lastValueFrom(
        this.shippingOrderService
          .getAttachedShippingOrderItems(
            this.subscriber.personId,
            this.selectedEditions
              .map(
                (s) =>
                  this.compositions.find((c) => c.editionId === s.editionId)
                    ?.subscriberEditionId
              )
              .concat(
                this.availableEditions.map(
                  (s) =>
                    this.compositions.find((c) => c.editionId === s.editionId)
                      ?.subscriberEditionId
                )
              )
          )
          .pipe(map((data) => data.result))
      );
    } catch (error) {
      this.shippingOrderItems = [];
    }
  }

  async findCoupons() {
    if (this.lastPayment?.paymentId)
      try {
        this.coupons = await lastValueFrom(
          this.subscriberService
            .findPaymentCoupons(this.lastPayment.paymentId)
            .pipe(map((data) => data.result))
        );
        this.shippingOrderItemsAvailable = (
          this.shippingOrderItemsAvailable || []
        ).concat(
          this.coupons
            ?.filter((c) => c.glampoints)
            .map(
              (c) =>
                ({
                  productVariantName: `Pontos do cupom ${c.couponName}`,
                  salePoint: c.glampoints,
                  shippingOrderTypeName: 'Bônus cupom'
                }) as ShippingOrderItemDetail
            )
        );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
        this.coupons = [];
      }
  }

  async findPaymentReward() {
    if (this.paymentHistory?.length)
      try {
        this.rewards = await lastValueFrom(
          this.rewardService
            .findSubscriptionPaymentReward(
              this.paymentHistory.map((p) => p.paymentId)
            )
            .pipe(map((data) => data.result))
        );
        if (this.rewards) {
          this.shippingOrderItemsAvailable = (
            this.shippingOrderItemsAvailable || []
          ).concat(
            this.rewards?.map(
              (r) =>
                ({
                  productVariantName: r.referenceName,
                  salePoint: r.points,
                  shippingOrderTypeName: 'Bônus ' + r.subtitle
                }) as ShippingOrderItemDetail
            )
          );
        }
      } catch (error) {
        AppDialogService.showErrorDialog(error);
        this.rewards = [];
      }
  }

  async findBadgeRewards() {
    try {
      this.badgeReward = await lastValueFrom(
        this.rewardService
          .findBadgeUpgradeReward(
            this.person?.personId || this.subscriber.personId,
            this.paymentHistory.reduce(
              (min, p) => Math.min(min, p.paymentId),
              Number.MAX_VALUE
            )
          )
          .pipe(map((data) => data.result))
      );
      if (this.badgeReward)
        this.shippingOrderItemsAvailable.push({
          productVariantName: this.badgeReward.referenceName,
          salePoint: this.badgeReward.points,
          shippingOrderTypeName:
            'Bônus ' +
            (this.badgeReward.subtitle || this.badgeReward.referenceName)
        } as ShippingOrderItemDetail);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  edition(subscriberEditionId: number) {
    return (
      this.compositions?.find(
        (c) => c.subscriberEditionId === subscriberEditionId
      )?.theme ||
      this.compositions?.find(
        (c) => c.subscriberEditionId === subscriberEditionId
      )?.editionId ||
      '-'
    );
  }

  setItemsToRefund() {
    this.shippingOrderItems = [
      ...this.shippingOrderItemsAvailable.filter(
        (i) =>
          (!i.subscriberEditionId &&
            (!this.availableEditions.length ||
              this.availableEditions.every((e) =>
                this.compositions.some(
                  (c) => c.editionId === e.editionId && c.dateCreatedMillenium
                )
              ))) ||
          (i.shippingOrderTypeId !== undefined &&
            this.selectedEditions.find((e) =>
              this.compositions.find(
                (c) =>
                  c.editionId === e.editionId &&
                  c.subscriberEditionId === i.subscriberEditionId
              )
            )) ||
          i.shippingOrderTypeId === undefined
      )
    ];
    if (this.shippingOrderItems?.length)
      this.dropdownFilters['shippingOrderTypeName'] = [
        ...new Set(
          this.shippingOrderItems.map((item) => item.shippingOrderTypeName)
        )
      ].map((type) => ({ label: type, value: type }) as DropdownFilter);
    this.shippingOrderItems?.sort(
      (s1, s2) =>
        s1.shippingOrderItemId - s2.shippingOrderItemId ||
        s1.productVariantName.localeCompare(s2.productVariantName)
    );
  }

  get gatewayUrl() {
    if (!this.lastPayment?.transactionId) return null;
    if (
      GatewayUtil.gatewayUrl[this.lastPayment.gatewayId] &&
      (environment.production ||
        [10, 12, 13, 17, 19, 21].includes(this.lastPayment.gatewayId))
    )
      return GatewayUtil.gatewayUrl[this.lastPayment.gatewayId]?.concat(
        this.lastPayment.transactionId
      );
    return null;
  }

  get shippingPrice() {
    return this.lastPayment?.billings?.at(0)?.shippingPrice || 0;
  }

  get monthlyAmount() {
    if (!this.lastPayment) return null;
    if (!this.lastPayment.creditCardId)
      return this.lastPayment.amount / this.subscriptionType.recurrenceMonths;
    return this.lastPayment.amount / this.lastPayment.installments;
  }

  get remainingAmount() {
    return this.hasFee || this.hasRenewalFee || this.availableEditions?.length
      ? this.monthlyAmount * this.selectedEditions?.length
      : 0;
  }

  get fee() {
    return (this.hasFee || this.hasRenewalFee) && this.remainingAmount
      ? (this.remainingAmount -
          this.selectedEditions.length * this.shippingPrice) *
          0.4
      : 0;
  }

  get refundAmount() {
    return (this.remainingAmount || this.lastPayment?.amount || 0) - this.fee;
  }

  get hasFee() {
    if (this.lastPayment?.creditCardPaymentTypeId === 6) return true;
    if (
      this.lastPayment &&
      (!this.firstEditionInPayment || !this.restored || !this.restoredNoFee)
    ) {
      return (
        this.diffDays(
          FormUtil.utcDate(new Date()).getTime() -
            new Date(
              this.firstEditionInPayment?.dateCreated ||
                this.lastPayment.dateCreated
            ).getTime()
        ) >= 7
      );
    }
    return false;
  }

  get noFeeLimit() {
    if (this.fee || !this.lastPayment) return null;
    const limit = new Date(this.lastPayment.dateCreated);
    limit.setDate(limit.getDate() + 7);
    return limit;
  }

  get refundedTransaction() {
    return (
      this.payment?.status &&
      (this.payment.status === 'refunded' ||
        this.chargedBack ||
        this.payment.refunded_amount > 0 ||
        this.payment.transactionAmountRefunded > 0)
      // this.refundStatusList.includes()
    );
  }

  get refundedAmount() {
    return (
      (this.refundedTransaction &&
        (this.payment.refunded_amount / 100 ||
          this.payment.transactionAmountRefunded)) ||
      (this.chargedBack &&
        (this.payment.amount / 100 || this.payment.transactionAmount))
    );
  }

  get chargedBack() {
    return (
      this.payment?.status === 'charged_back' ||
      this.payment?.status === 'chargedback'
    );
  }

  get paymentStatus() {
    return this.refundedTransaction && this.payment.status === 'paid'
      ? 'refunded'
      : this.payment.status;
  }

  get restored() {
    return (
      this.firstEditionInPayment?.subscriberEditionStatusId === 11 ||
      this.firstEditionInPayment?.statusMillenium?.toUpperCase() ===
        'DEVOLVIDO' ||
      this.firstEditionInPayment?.statusMillenium?.toUpperCase() ===
        'CANCELADO' ||
      this.firstEditionInPayment?.statusMillenium?.toUpperCase() ===
        'EM DEVOLUÇÃO'
    );
  }

  get restoredNoFee() {
    return this.restored &&
      this.firstEditionInPayment &&
      (!this.secondEditionInPayment ||
        this.secondEditionInPayment.subscriberEditionStatusId < 0 ||
        this.secondEditionInPayment.subscriberEditionStatusId > 6)
      ? this.firstEditionInPayment?.statusMillenium?.toUpperCase() ===
          'DEVOLVIDO' ||
          this.diffDays(
            FormUtil.utcDate(
              this.firstEditionInPayment.dateUpdated ||
                this.firstEditionInPayment.dateCreated
            ).getTime() -
              FormUtil.utcDate(
                this.firstEditionInPayment.finalDeliveryDate ||
                  this.firstEditionInPayment.dateUpdatedMillenium ||
                  this.firstEditionInPayment.dateCreatedMillenium
              ).getTime()
          ) < 7
      : false;
  }

  get lastPayment() {
    return this.paymentHistory
      ?.filter(
        (p) =>
          p.transactionStatusId === 6 &&
          [0, 1, 2, 6].includes(p.creditCardPaymentTypeId)
      )
      ?.reduce((last: PaymentHistory, p: PaymentHistory) => {
        if (
          !last ||
          new Date(last.dateCreated).getTime() <
            new Date(p.dateCreated).getTime()
        ) {
          last = p;
        }
        return last;
      }, null);
  }

  get firstEditionInPayment() {
    if (!this.lastPayment?.subscriptionTypeId) return null;
    const subscriptionType = this.subscriptionTypes?.find(
      (st) => st.subscriptionTypeId === this.lastPayment.subscriptionTypeId
    );
    let first = this.lastPayment.billings?.at(0)?.editionId;
    if (
      subscriptionType?.recurrenceMonths === 1 &&
      subscriptionType?.cycle > 1
    ) {
      const editions: SubscriberPaymentDetail['billings'] = this.paymentHistory
        ?.filter(
          (p) =>
            p.subscriptionTypeId === subscriptionType.subscriptionTypeId &&
            p.transactionStatusId === 6 &&
            [0, 1, 2, 6].includes(p.creditCardPaymentTypeId) &&
            p.billings?.length
        )
        .reduce((billings, p) => (billings = billings.concat(p.billings)), []);
      editions.sort((e1, e2) => e1.editionId - e2.editionId);
      first = editions[0].editionId;
    }
    return this.editionFromBilling(first);
  }

  get secondEditionInPayment() {
    if (!this.lastPayment?.subscriptionTypeId) return null;
    const subscriptionType = this.subscriptionTypes?.find(
      (st) => st.subscriptionTypeId === this.lastPayment.subscriptionTypeId
    );
    let second = this.lastPayment.billings?.at(1)?.editionId;
    if (
      subscriptionType?.recurrenceMonths === 1 &&
      subscriptionType?.cycle > 1
    ) {
      const editions: SubscriberPaymentDetail['billings'] = this.paymentHistory
        ?.filter(
          (p) =>
            p.subscriptionTypeId === subscriptionType.subscriptionTypeId &&
            p.transactionStatusId === 6 &&
            [0, 1, 2, 6].includes(p.creditCardPaymentTypeId) &&
            p.billings?.length
        )
        .reduce((billings, p) => (billings = billings.concat(p.billings)), []);
      editions.sort((e1, e2) => e1.editionId - e2.editionId);
      second = editions.at(1)?.editionId;
    }
    return this.editionFromBilling(second);
  }

  get firstRenewal() {
    if (!this.lastPayment || !this.subscriptionTypes) return false;
    const payments = this.paymentHistory?.filter(
      (p) =>
        p.transactionStatusId === 6 &&
        [0, 1, 2, 6].includes(p.creditCardPaymentTypeId) &&
        new Date(p.dateCreated).getTime() <
          new Date(this.lastPayment.dateCreated).getTime()
    );
    payments.sort(
      (p1, p2) =>
        new Date(p1.dateCreated).getTime() - new Date(p2.dateCreated).getTime()
    );
    const subscriptionTypes = payments.reduce(
      (plans: SubscriptionType[], p) => {
        if (
          !plans.some(
            (plan) => plan.subscriptionTypeId === p.subscriptionTypeId
          )
        ) {
          const plan = this.subscriptionTypes?.find(
            (st) => st.subscriptionTypeId === p.subscriptionTypeId
          );
          if (plan) plans.push(plan);
        }
        return plans;
      },
      []
    );
    return (
      payments.length === 1 ||
      (subscriptionTypes.length === 1 &&
        subscriptionTypes[0].recurrenceMonths === 1 &&
        subscriptionTypes[0].cycle > 1 &&
        (payments.length === subscriptionTypes[0].cycle ||
          this.diffDays(
            FormUtil.utcDate(new Date()).getTime() -
              new Date(payments[0].dateCreated).getTime()
          ) < 7))
    );
  }

  get hasRenewalFee() {
    return (
      !this.firstRenewal &&
      this.lastPayment &&
      [1, 2, 6].includes(this.lastPayment.creditCardPaymentTypeId)
    );
  }

  get montlySubscriptionType() {
    return this.lastPayment?.billings?.length
      ? this.subscriptionTypes?.find(
          (st) =>
            st.subscriptionId.toString() ===
              this.lastPayment?.billings?.at(0).editionId.toString()[0] &&
            st.installments === 1 &&
            st.cycle === 1
        )
      : null;
  }

  get subscriptionType() {
    return this.subscriptionTypes?.find(
      (st) => st.subscriptionTypeId === this.subscriber?.subscriptionTypeId
    );
  }

  get pendingAnnualMonthly() {
    return this.subscriptionType?.cycle > 1 &&
      this.subscriptionType?.recurrenceMonths === 1
      ? FormUtil.diff_months(
          this.currentEditionBillings?.length
            ? new Date(
                new Date(new Date().setDate(1)).setMonth(
                  new Date().getMonth() + 1
                )
              )
            : new Date(),
          FormUtil.utcDate(this.subscriber.nextCycleDate)
        )
      : 0;
  }

  get currentEditionBillings() {
    return this.paymentHistory
      ?.filter((h) =>
        h.billings.some(
          (b) =>
            b.subscriberBillingStatus === 1 &&
            b.editionId ===
              EditionId.currentEdition(EditionId.subscriptionId(b.editionId))
        )
      )
      ?.map((p) =>
        p.billings.filter(
          (b) =>
            b.subscriberBillingStatus === 1 &&
            b.editionId ===
              EditionId.currentEdition(EditionId.subscriptionId(b.editionId))
        )
      )
      ?.reduce((list, b) => list.concat(b), []);
  }

  get amountToCharge(): number {
    return (
      this.pendingAnnualMonthly *
      ((this.lastPayment?.amount || 0) - (this.shippingPrice || 0)) *
      0.4
    );
  }

  get pix() {
    return !this.lastPayment?.creditCardId;
  }

  get glampoints() {
    return this.shippingOrderItems
      ?.filter(
        (s) =>
          s.shippingOrderTypeId === undefined || s.shippingOrderTypeId === 3
      )
      ?.reduce(
        (sum, i) =>
          (sum +=
            (i.shippingOrderTypeId !== 3 ? -i.salePoint : i.salePoint) || 0),
        0
      );
  }

  get automaticRefund() {
    return (
      this.payment &&
      !this.refundedTransaction &&
      ![19, 20].includes(this.lastPayment?.gatewayId)
    );
  }
}
